// Hand-crafted 24×24 SVG icons for IFC classes.
// All paths use `currentColor` so the CSS `color` property drives the tint.
// Outline style: stroke-width 1.5, round caps/joins, fill="none" unless noted.
//
// Each entry is the *inner* SVG (children of <svg>); the <IfcIcon> wrapper
// supplies the root <svg width height viewBox stroke ...> attributes.

const STROKE_ATTRS =
    'stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none"';

export const IFC_ICON_SVGS: Record<string, string> = {
    // ── Fallback ──────────────────────────────────────────
    cube: `
		<path d="M12 3 L21 7.5 L21 16.5 L12 21 L3 16.5 L3 7.5 Z" ${STROKE_ATTRS}/>
		<path d="M3 7.5 L12 12 L21 7.5" ${STROKE_ATTRS}/>
		<path d="M12 12 L12 21" ${STROKE_ATTRS}/>
	`,

    // ── Spatial containers ────────────────────────────────
    project: `
		<circle cx="6" cy="6" r="2.2" ${STROKE_ATTRS}/>
		<circle cx="18" cy="12" r="2.2" ${STROKE_ATTRS}/>
		<circle cx="6" cy="18" r="2.2" ${STROKE_ATTRS}/>
		<path d="M7.8 7 L15.8 10.8" ${STROKE_ATTRS}/>
		<path d="M7.8 17 L15.8 13.2" ${STROKE_ATTRS}/>
	`,
    site: `
		<path d="M2 20 Q7 17 12 20 T22 20" ${STROKE_ATTRS}/>
		<path d="M12 4 A3 3 0 1 1 12 10 A3 3 0 1 1 12 4 Z" ${STROKE_ATTRS}/>
		<path d="M12 10 L12 17" ${STROKE_ATTRS}/>
	`,
    building: `
		<rect x="5" y="3" width="14" height="18" ${STROKE_ATTRS}/>
		<rect x="7.5" y="6" width="2" height="2" ${STROKE_ATTRS}/>
		<rect x="14.5" y="6" width="2" height="2" ${STROKE_ATTRS}/>
		<rect x="7.5" y="11" width="2" height="2" ${STROKE_ATTRS}/>
		<rect x="14.5" y="11" width="2" height="2" ${STROKE_ATTRS}/>
		<rect x="10" y="16" width="4" height="5" ${STROKE_ATTRS}/>
	`,
    storey: `
		<path d="M3 7 H21" ${STROKE_ATTRS}/>
		<path d="M3 12 H21" ${STROKE_ATTRS}/>
		<path d="M3 17 H21" ${STROKE_ATTRS}/>
	`,
    space: `
		<rect x="4" y="5" width="16" height="14" ${STROKE_ATTRS} stroke-dasharray="3 2"/>
	`,
    zone: `
		<rect x="3" y="3" width="12" height="12" rx="1" ${STROKE_ATTRS}/>
		<rect x="9" y="9" width="12" height="12" rx="1" ${STROKE_ATTRS}/>
	`,

    // ── Walls ─────────────────────────────────────────────
    wall: `
		<rect x="4" y="3" width="4" height="18" fill="currentColor" opacity="0.3"/>
		<path d="M4 3 V21 M8 3 V21" ${STROKE_ATTRS}/>
		<rect x="16" y="3" width="4" height="18" fill="currentColor" opacity="0.3"/>
		<path d="M16 3 V21 M20 3 V21" ${STROKE_ATTRS}/>
	`,
    curtainWall: `
		<rect x="3" y="3" width="18" height="18" ${STROKE_ATTRS}/>
		<path d="M9 3 V21 M15 3 V21 M3 9 H21 M3 15 H21" ${STROKE_ATTRS}/>
	`,

    // ── Openings ──────────────────────────────────────────
    door: `
		<path d="M5 21 V5 H13 V21" ${STROKE_ATTRS}/>
		<path d="M5 21 A16 16 0 0 1 13 5" ${STROKE_ATTRS} stroke-dasharray="2 2"/>
		<circle cx="11.5" cy="13" r="0.7" fill="currentColor"/>
	`,
    window: `
		<rect x="4" y="4" width="16" height="16" ${STROKE_ATTRS}/>
		<path d="M12 4 V20 M4 12 H20" ${STROKE_ATTRS}/>
	`,
    opening: `
		<rect x="4" y="4" width="16" height="16" ${STROKE_ATTRS} stroke-dasharray="3 3"/>
	`,

    // ── Horizontals ───────────────────────────────────────
    slab: `
		<rect x="3" y="9" width="18" height="6" fill="currentColor" opacity="0.3"/>
		<rect x="3" y="9" width="18" height="6" ${STROKE_ATTRS}/>
		<path d="M5 9 L3 11 M8 9 L6 11 M11 9 L9 11 M14 9 L12 11 M17 9 L15 11 M20 9 L18 11" ${STROKE_ATTRS}/>
	`,
    roof: `
		<path d="M3 18 L12 6 L21 18" ${STROKE_ATTRS}/>
		<path d="M5 18 H19" ${STROKE_ATTRS}/>
		<path d="M10 18 V21 H14 V18" ${STROKE_ATTRS}/>
	`,
    plate: `
		<rect x="3" y="10" width="18" height="4" fill="currentColor" opacity="0.3"/>
		<rect x="3" y="10" width="18" height="4" ${STROKE_ATTRS}/>
	`,

    // ── Structural members ────────────────────────────────
    column: `
		<path d="M7 3 H17 V6 H14 V18 H17 V21 H7 V18 H10 V6 H7 Z" fill="currentColor" opacity="0.3"/>
		<path d="M7 3 H17 V6 H14 V18 H17 V21 H7 V18 H10 V6 H7 Z" ${STROKE_ATTRS}/>
	`,
    beam: `
		<path d="M3 7 V17 H6 V14 H18 V17 H21 V7 H18 V10 H6 V7 Z" fill="currentColor" opacity="0.3"/>
		<path d="M3 7 V17 H6 V14 H18 V17 H21 V7 H18 V10 H6 V7 Z" ${STROKE_ATTRS}/>
	`,
    footing: `
		<path d="M10 3 V13 H14 V3" ${STROKE_ATTRS}/>
		<rect x="3" y="13" width="18" height="6" fill="currentColor" opacity="0.3"/>
		<rect x="3" y="13" width="18" height="6" ${STROKE_ATTRS}/>
	`,
    pile: `
		<rect x="10" y="3" width="4" height="16" fill="currentColor" opacity="0.3"/>
		<rect x="10" y="3" width="4" height="16" ${STROKE_ATTRS}/>
		<path d="M8 19 L12 22 L16 19" ${STROKE_ATTRS}/>
	`,
    member: `
		<path d="M4 20 L20 4" ${STROKE_ATTRS} stroke-width="3"/>
	`,
    tendon: `
		<path d="M3 12 Q6 8 9 12 T15 12 T21 12" ${STROKE_ATTRS}/>
		<path d="M3 16 Q6 12 9 16 T15 16 T21 16" ${STROKE_ATTRS}/>
	`,

    // ── Circulation ───────────────────────────────────────
    stair: `
		<path d="M3 21 H8 V17 H13 V13 H18 V9 H21 V6" ${STROKE_ATTRS}/>
		<path d="M3 21 L3 24 M8 17 V21 M13 13 V17 M18 9 V13" ${STROKE_ATTRS} opacity="0.35"/>
	`,
    ramp: `
		<path d="M3 20 L21 6" ${STROKE_ATTRS}/>
		<path d="M3 20 H21" ${STROKE_ATTRS}/>
		<path d="M6 20 V17.6 M10 20 V14.4 M14 20 V11.2 M18 20 V8" ${STROKE_ATTRS} opacity="0.35"/>
	`,
    railing: `
		<path d="M2 7 H22" ${STROKE_ATTRS} stroke-width="2"/>
		<path d="M5 7 V20 M10 7 V20 M15 7 V20 M20 7 V20" ${STROKE_ATTRS}/>
		<path d="M2 20 H22" ${STROKE_ATTRS}/>
	`,

    // ── MEP ───────────────────────────────────────────────
    pipe: `
		<circle cx="12" cy="12" r="8" ${STROKE_ATTRS}/>
		<circle cx="12" cy="12" r="4" ${STROKE_ATTRS}/>
	`,
    duct: `
		<rect x="2" y="8" width="20" height="8" rx="1" ${STROKE_ATTRS}/>
		<path d="M6 12 H18" ${STROKE_ATTRS} stroke-dasharray="2 2"/>
	`,
    valve: `
		<circle cx="12" cy="12" r="5" ${STROKE_ATTRS}/>
		<path d="M3 12 H7 M17 12 H21" ${STROKE_ATTRS}/>
		<path d="M12 7 V17" ${STROKE_ATTRS}/>
	`,
    cable: `
		<path d="M3 12 Q7 4 12 12 T21 12" ${STROKE_ATTRS}/>
	`,
    sanitary: `
		<path d="M12 3 Q5 12 12 20 Q19 12 12 3 Z" ${STROKE_ATTRS}/>
	`,
    lamp: `
		<path d="M9 20 H15" ${STROKE_ATTRS}/>
		<path d="M10 17 H14" ${STROKE_ATTRS}/>
		<path d="M7 11 A5 5 0 1 1 17 11 Q15 13 14.5 16 H9.5 Q9 13 7 11 Z" ${STROKE_ATTRS}/>
	`,
    outlet: `
		<rect x="4" y="4" width="16" height="16" rx="2" ${STROKE_ATTRS}/>
		<circle cx="10" cy="12" r="1.2" fill="currentColor"/>
		<circle cx="14" cy="12" r="1.2" fill="currentColor"/>
	`,
    fitting: `
		<path d="M14.5 4 A5 5 0 1 0 19.5 9 L20.5 8 V11.5 H17 L19.5 9" ${STROKE_ATTRS}/>
		<path d="M14.5 9.5 L4 20 L6 22 L16.5 11.5" ${STROKE_ATTRS}/>
	`,
    flow: `
		<path d="M3 12 H18" ${STROKE_ATTRS}/>
		<path d="M14 7 L21 12 L14 17" ${STROKE_ATTRS}/>
	`,

    // ── Finishes ──────────────────────────────────────────
    covering: `
		<rect x="3" y="6" width="12" height="5" rx="1" ${STROKE_ATTRS}/>
		<path d="M4 6 H14 M4 11 H14" ${STROKE_ATTRS} opacity="0.4"/>
		<path d="M15 8.5 H19 V13 H16.5" ${STROKE_ATTRS}/>
		<path d="M16.5 13 V21" ${STROKE_ATTRS} stroke-width="2"/>
	`,
    furnishing: `
		<path d="M6 9 V4 H18 V9" ${STROKE_ATTRS}/>
		<rect x="4" y="9" width="16" height="6" rx="1" ${STROKE_ATTRS}/>
		<path d="M6 15 V21 M18 15 V21" ${STROKE_ATTRS}/>
	`,

    // ── Assemblies ────────────────────────────────────────
    assembly: `
		<rect x="3" y="13" width="8" height="8" ${STROKE_ATTRS}/>
		<rect x="13" y="13" width="8" height="8" ${STROKE_ATTRS}/>
		<rect x="8" y="3" width="8" height="8" ${STROKE_ATTRS}/>
	`,
};
